import type { BrokerageHolding } from "../types";

export const BASE_MAINTENANCE_PERCENT = 25;
export const CRYPTO_MAINTENANCE_PERCENT = 60;
export const LEVERAGED_ETF_MAINTENANCE_PERCENT = 75;

export type MaintenanceClass = "base" | "crypto" | "leveraged_etf";

// Common leveraged/inverse ETF families. Name-based detection below covers newly listed
// funds when Plaid supplies a descriptive name; the symbols cover terse brokerage data.
const LEVERAGED_ETF_SYMBOLS = new Set([
  "BITX", "BOIL", "DUST", "ERX", "ERY", "FAS", "FAZ", "KOLD", "LABD", "LABU",
  "MSTU", "NUGT", "QLD", "QID", "SOXL", "SOXS", "SPXL", "SPXS", "SPXU", "SQQQ",
  "TBT", "TECL", "TECS", "TMF", "TNA", "TQQQ", "TZA", "UDOW", "UPRO", "URE",
  "USD", "UWM", "YANG", "YINN",
]);

const CRYPTO_FUND_SYMBOLS = new Set([
  "ARKB", "BITB", "BITO", "BTC", "BTCO", "ETHA", "ETHE", "FBTC", "GBTC", "HODL",
  "IBIT",
]);

export function maintenanceClass(holding: BrokerageHolding): MaintenanceClass {
  const symbol = holding.symbol?.trim().toUpperCase() ?? "";
  const name = holding.name?.trim() ?? "";
  const type = holding.security_type?.trim().toLowerCase() ?? "";

  if (
    LEVERAGED_ETF_SYMBOLS.has(symbol)
    || /(?:\b[+-]?[23]x\b|\bultra(?:pro|short)?\b|\bleveraged\b|\bdaily\s+(?:bull|bear)\b)/i.test(name)
  ) return "leveraged_etf";

  if (
    type === "cryptocurrency"
    || CRYPTO_FUND_SYMBOLS.has(symbol)
    || /\b(?:bitcoin|ether|ethereum|cryptocurrency|crypto)\b/i.test(name)
  ) return "crypto";

  return "base";
}

export function maintenancePercent(holding: BrokerageHolding): number {
  const classification = maintenanceClass(holding);
  if (classification === "leveraged_etf") return LEVERAGED_ETF_MAINTENANCE_PERCENT;
  if (classification === "crypto") return CRYPTO_MAINTENANCE_PERCENT;
  return BASE_MAINTENANCE_PERCENT;
}

export interface PortfolioMaintenance {
  percent: number;
  classifiedValue: number;
  totalValue: number;
  counts: Record<MaintenanceClass, number>;
}

/** Value-weighted maintenance requirement for priced holdings. Unpriced value uses the
    conservative base rate so the calculation still covers the full supplied portfolio. */
export function portfolioMaintenance(
  holdings: BrokerageHolding[],
  portfolioValue: number,
): PortfolioMaintenance {
  const priced = holdings.filter((holding) => (holding.value ?? 0) > 0);
  const classifiedValue = priced.reduce((sum, holding) => sum + (holding.value ?? 0), 0);
  const totalValue = Math.max(portfolioValue, classifiedValue, 0);
  const unclassifiedValue = Math.max(0, totalValue - classifiedValue);
  const requiredEquity = priced.reduce(
    (sum, holding) => sum + (holding.value ?? 0) * maintenancePercent(holding) / 100,
    unclassifiedValue * BASE_MAINTENANCE_PERCENT / 100,
  );
  const counts: Record<MaintenanceClass, number> = {
    base: 0,
    crypto: 0,
    leveraged_etf: 0,
  };
  priced.forEach((holding) => {
    counts[maintenanceClass(holding)] += 1;
  });
  return {
    percent: totalValue > 0 ? requiredEquity / totalValue * 100 : BASE_MAINTENANCE_PERCENT,
    classifiedValue,
    totalValue,
    counts,
  };
}
